import React, { useState, useEffect } from 'react';
import Sidebar from '../../../shared/components/Sidebar';
import RichTextEditor from '../../../shared/components/RichTextEditor';
import CloudinaryImageUpload from '../../../shared/components/CloudinaryImageUpload';
import { Select, MenuItem, FormControl, InputLabel, TextField, Button, CircularProgress, Snackbar, Alert, FormControlLabel, Switch } from '@mui/material';
import { BiSolidPaperPlane } from 'react-icons/bi';
import { useParams, useNavigate } from 'react-router-dom';

import './articles.css';
import useArticlesViewModel from '../hooks/useArticlesViewModel';
import GetArticleCategoriesUseCase from '../use-cases/GetArticleCategoriesUseCase';
import FirebaseArticleRepository from '../api/FirebaseArticleRepository';
import BackButton from '../../../shared/components/Backbtn';

const EditArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // const { db } = useFirebase(); // No longer needed

  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [linkName, setLinkName] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [isTrending, setIsTrending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quickReadsCategories, setQuickReadsCategories] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { updateArticle, getArticleDetails, error: vmError } = useArticlesViewModel();
  useEffect(() => {
    const getArticleCategoriesUseCase = new GetArticleCategoriesUseCase(new FirebaseArticleRepository());
    const fetchCategoriesAndArticle = async () => {
      setLoading(true);
      try {
        // Fetch categories
        const categories = await getArticleCategoriesUseCase.execute();
        setQuickReadsCategories(categories);

        // Fetch article details
        const article = await getArticleDetails(id);
        if (article) {
          setCategory(article.category);
          setTitle(article.title);
          setLink(article.link || '');
          setLinkName(article.linkName || '');
          setDescription(article.description);
          setImageUrl(article.imageUrl || null);
          setIsTrending(article.isTrending || false);
        } else {
          setSnackbarMessage('Article not found!');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          navigate('/articles');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbarMessage('Failed to load article for editing.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        navigate('/articles');
      } finally {
        setLoading(false);
      }
    };

    fetchCategoriesAndArticle();
  }, [id, navigate, getArticleDetails]);

  const handleSubmit = async () => {
    if (!category || !title || !description.trim()) {
      setSnackbarMessage('Please fill in all required fields (Category, Title, and Description).');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
  
    setLoading(true);

    try {
      const updates = {
        category,
        title,
        link,
        linkName,
        description,
        imageUrl,
        isTrending,
      };

      await updateArticle(id, updates);
  
      setSnackbarMessage('Article updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate('/articles');
    } catch (error) {
      console.error('Error:', error);
      setSnackbarMessage(`Failed to update article: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="dashboard--content">
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content">
        <div className="flex justify-end gap-3 mb-4">
          <BackButton tooltip="Back" />
        </div>
        <Snackbar
          open={snackbarOpen || !!vmError}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={vmError ? 'error' : snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage || vmError?.message || vmError}
          </Alert>
        </Snackbar>
        <div className="section">
          {/* Category Select */}
          <FormControl variant="outlined" fullWidth className="form-control">
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
            >
              {quickReadsCategories.length > 0 ? (
                quickReadsCategories.map((cat, index) => (
                  <MenuItem key={index} value={cat.toLowerCase()}>{cat}</MenuItem>
                ))
              ) : (
                <MenuItem disabled>No categories available</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Title Input */}
          <FormControl fullWidth className="mt-3 form-control">
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="outlined"
            />
          </FormControl>

          {/* Link Input */}
          <FormControl fullWidth className="mt-3 form-control">
            <TextField
              label="Link (Optional)"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              variant="outlined"
            />
          </FormControl>

          {/* Link Name Input */}
          <FormControl fullWidth className="mt-3 form-control">
            <TextField
              label="Link Name (Optional)"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
              variant="outlined"
              placeholder="e.g. Apply, Read More, Visit"
            />
          </FormControl>

          {/* Cloudinary Image Upload */}
          <CloudinaryImageUpload
            label="Article Image (Optional)"
            onUploadComplete={(url) => setImageUrl(url)}
            existingImageUrl={imageUrl}
          />

          {/* Is Trending Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={isTrending}
                onChange={(e) => setIsTrending(e.target.checked)}
              />
            }
            label="Mark as Trending"
            sx={{ mt: 2 }}
          />

          {/* Rich Text Editor */}
          <div className="mt-4">
            <InputLabel
              shrink
              sx={{
                position: 'relative',
                transform: 'none',
                marginBottom: '8px',
                color: 'rgba(0, 0, 0, 0.6)',
                fontSize: '0.875rem',
              }}
            >
              Description <span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Write your article content here..."
              minHeight="300px"
            />
          </div>

          {/* Submit Button */}
          <Button
            variant="contained"
            endIcon={<BiSolidPaperPlane />}
            className="mt-3 send-button"
            onClick={handleSubmit}
            disabled={loading}
            fullWidth
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Article'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditArticle;
